import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Plus, Edit, Trash2, Save, RefreshCw, ChevronRight, ChevronDown, Palette, Monitor, Film, Megaphone, Shield } from 'lucide-react';
import api from '../../lib/axios';
import { toast } from 'sonner';

const ICON_MAP = { 'Palette': Palette, 'Monitor': Monitor, 'Film': Film, 'Megaphone': Megaphone, 'Shield': Shield };

const CMSServicesPage = () => {
  const [families, setFamilies] = useState([]);
  const [solutions, setSolutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedFamily, setExpandedFamily] = useState(null);
  const [editModal, setEditModal] = useState({ open: false, type: null, data: null });
  const [deleteModal, setDeleteModal] = useState({ open: false, type: null, id: null });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [famRes, solRes] = await Promise.all([api.get('/cms/service-families'), api.get('/cms/solutions')]);
      setFamilies(famRes.data); setSolutions(solRes.data);
    } catch (error) { toast.error('Erreur lors du chargement'); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    const { type, data } = editModal;
    try {
      if (data.id) {
        await api.put(`/cms/${type === 'family' ? 'service-families' : 'solutions'}/${data.id}`, data);
      } else {
        await api.post(`/cms/${type === 'family' ? 'service-families' : 'solutions'}`, data);
      }
      toast.success('Enregistré avec succès');
      loadData(); setEditModal({ open: false, type: null, data: null });
    } catch (error) { toast.error('Erreur lors de la sauvegarde'); }
  };

  const handleDelete = async () => {
    const { type, id } = deleteModal;
    try {
      await api.delete(`/cms/${type === 'family' ? 'service-families' : 'solutions'}/${id}`);
      toast.success('Supprimé avec succès'); loadData();
      setDeleteModal({ open: false, type: null, id: null });
    } catch (error) { toast.error('Erreur lors de la suppression'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><RefreshCw className="h-8 w-8 animate-spin text-cyan-500" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div><h1 className="text-2xl font-bold text-slate-900">Services</h1><p className="text-slate-500">Gérez vos familles de services et solutions</p></div>
        <Button onClick={() => setEditModal({ open: true, type: 'family', data: { is_active: true, order: families.length + 1 } })} className="bg-gradient-to-r from-cyan-500 to-blue-600"><Plus className="w-4 h-4 mr-2" />Nouvelle famille</Button>
      </div>

      <div className="space-y-4">
        {families.map((family) => {
          const Icon = ICON_MAP[family.icon] || Palette;
          const familySolutions = solutions.filter(s => s.family_id === family.id);
          const isExpanded = expandedFamily === family.id;
          return (
            <Card key={family.id}>
              <CardHeader className="cursor-pointer" onClick={() => setExpandedFamily(isExpanded ? null : family.id)}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg bg-gradient-to-br ${family.color} text-white`}><Icon className="h-5 w-5" /></div>
                    <div><CardTitle className="text-base">{family.title}</CardTitle><CardDescription>{family.description}</CardDescription></div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">{familySolutions.length} solutions</Badge>
                    <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); setEditModal({ open: true, type: 'family', data: family }); }}><Edit className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); setDeleteModal({ open: true, type: 'family', id: family.id }); }}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                    {isExpanded ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
                  </div>
                </div>
              </CardHeader>
              {isExpanded && (
                <CardContent className="border-t pt-4">
                  <div className="flex justify-between items-center mb-4">
                    <span className="text-sm font-medium">Solutions</span>
                    <Button size="sm" variant="outline" onClick={() => setEditModal({ open: true, type: 'solution', data: { family_id: family.id, is_active: true, order: familySolutions.length + 1, features: [], includes: [], ideal_for: [] } })}><Plus className="h-3 w-3 mr-1" />Ajouter</Button>
                  </div>
                  {familySolutions.length === 0 ? <p className="text-sm text-slate-400 italic">Aucune solution</p> : (
                    <div className="grid gap-2">
                      {familySolutions.map(sol => (
                        <div key={sol.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div><span className="font-medium">{sol.name}</span>{sol.badge && <Badge className="ml-2" variant="secondary">{sol.badge}</Badge>}<p className="text-sm text-slate-500">{sol.price_range}</p></div>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="sm" onClick={() => setEditModal({ open: true, type: 'solution', data: sol })}><Edit className="h-4 w-4" /></Button>
                            <Button variant="ghost" size="sm" onClick={() => setDeleteModal({ open: true, type: 'solution', id: sol.id })}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              )}
            </Card>
          );
        })}
      </div>

      {/* Edit Modal */}
      <Dialog open={editModal.open} onOpenChange={(open) => !open && setEditModal({ open: false, type: null, data: null })}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editModal.data?.id ? 'Modifier' : 'Nouvelle'} {editModal.type === 'family' ? 'famille de service' : 'solution'}</DialogTitle></DialogHeader>
          {editModal.type === 'family' && editModal.data && (
            <div className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2"><Label>Titre</Label><Input value={editModal.data.title || ''} onChange={(e) => setEditModal({...editModal, data: {...editModal.data, title: e.target.value}})} /></div>
                <div className="space-y-2"><Label>Titre court</Label><Input value={editModal.data.short_title || ''} onChange={(e) => setEditModal({...editModal, data: {...editModal.data, short_title: e.target.value}})} /></div>
              </div>
              <div className="space-y-2"><Label>Description</Label><Textarea value={editModal.data.description || ''} onChange={(e) => setEditModal({...editModal, data: {...editModal.data, description: e.target.value}})} /></div>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2"><Label>Icône</Label><Select value={editModal.data.icon || ''} onValueChange={(v) => setEditModal({...editModal, data: {...editModal.data, icon: v}})}><SelectTrigger><SelectValue placeholder="Choisir..." /></SelectTrigger><SelectContent><SelectItem value="Palette">Palette</SelectItem><SelectItem value="Monitor">Monitor</SelectItem><SelectItem value="Film">Film</SelectItem><SelectItem value="Megaphone">Megaphone</SelectItem><SelectItem value="Shield">Shield</SelectItem></SelectContent></Select></div>
                <div className="space-y-2"><Label>Couleur</Label><Input value={editModal.data.color || ''} onChange={(e) => setEditModal({...editModal, data: {...editModal.data, color: e.target.value}})} placeholder="from-pink-500 to-rose-600" /></div>
              </div>
            </div>
          )}
          {editModal.type === 'solution' && editModal.data && (
            <div className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2"><Label>Nom</Label><Input value={editModal.data.name || ''} onChange={(e) => setEditModal({...editModal, data: {...editModal.data, name: e.target.value}})} /></div>
                <div className="space-y-2"><Label>Badge</Label><Input value={editModal.data.badge || ''} onChange={(e) => setEditModal({...editModal, data: {...editModal.data, badge: e.target.value}})} placeholder="Populaire" /></div>
              </div>
              <div className="space-y-2"><Label>Description</Label><Textarea value={editModal.data.description || ''} onChange={(e) => setEditModal({...editModal, data: {...editModal.data, description: e.target.value}})} /></div>
              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2"><Label>Prix à partir de</Label><Input type="number" value={editModal.data.price_from || ''} onChange={(e) => setEditModal({...editModal, data: {...editModal.data, price_from: parseFloat(e.target.value) || 0}})} /></div>
                <div className="space-y-2"><Label>Fourchette</Label><Input value={editModal.data.price_range || ''} onChange={(e) => setEditModal({...editModal, data: {...editModal.data, price_range: e.target.value}})} placeholder="1 500€ - 4 000€" /></div>
                <div className="space-y-2"><Label>Délai</Label><Input value={editModal.data.delivery_time || ''} onChange={(e) => setEditModal({...editModal, data: {...editModal.data, delivery_time: e.target.value}})} placeholder="2-4 semaines" /></div>
              </div>
              <div className="space-y-2"><Label>Caractéristiques (une par ligne)</Label><Textarea value={(editModal.data.features || []).join('\n')} onChange={(e) => setEditModal({...editModal, data: {...editModal.data, features: e.target.value.split('\n').filter(f => f.trim())}})} rows={3} /></div>
              
              {/* New fields for solutions */}
              <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200 space-y-4">
                <Label className="text-base font-semibold text-emerald-800">✨ Ce qui est inclus</Label>
                <Textarea 
                  value={(editModal.data.includes || []).join('\n')} 
                  onChange={(e) => setEditModal({...editModal, data: {...editModal.data, includes: e.target.value.split('\n').filter(f => f.trim())}})} 
                  rows={3}
                  placeholder="Design sur-mesure&#10;Fichiers sources&#10;Support technique 30 jours"
                />
              </div>
              
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 space-y-4">
                <Label className="text-base font-semibold text-blue-800">🎯 Pourquoi le faire ?</Label>
                <Textarea 
                  value={editModal.data.why_do_it || ''} 
                  onChange={(e) => setEditModal({...editModal, data: {...editModal.data, why_do_it: e.target.value}})} 
                  rows={3}
                  placeholder="Expliquez les bénéfices et l'importance de cette solution..."
                />
              </div>
              
              <div className="grid gap-4 md:grid-cols-2">
                <div className="p-4 bg-cyan-50 rounded-lg border border-cyan-200 space-y-4">
                  <Label className="text-base font-semibold text-cyan-800">✅ Idéal pour</Label>
                  <Textarea 
                    value={(editModal.data.ideal_for || []).join('\n')} 
                    onChange={(e) => setEditModal({...editModal, data: {...editModal.data, ideal_for: e.target.value.split('\n').filter(f => f.trim())}})} 
                    rows={4}
                    placeholder="Startups&#10;E-commerce&#10;PME en croissance"
                  />
                </div>
                <div className="p-4 bg-orange-50 rounded-lg border border-orange-200 space-y-4">
                  <Label className="text-base font-semibold text-orange-800">⚠️ Moins adapté pour</Label>
                  <Textarea 
                    value={(editModal.data.not_ideal_for || []).join('\n')} 
                    onChange={(e) => setEditModal({...editModal, data: {...editModal.data, not_ideal_for: e.target.value.split('\n').filter(f => f.trim())}})} 
                    rows={4}
                    placeholder="Projets simples&#10;Budgets très limités&#10;Besoins temporaires"
                  />
                </div>
              </div>
              
              <div className="p-4 bg-purple-50 rounded-lg border border-purple-200 space-y-4">
                <Label className="text-base font-semibold text-purple-800">📅 Quand le faire ?</Label>
                <Textarea 
                  value={editModal.data.when_to_do_it || ''} 
                  onChange={(e) => setEditModal({...editModal, data: {...editModal.data, when_to_do_it: e.target.value}})} 
                  rows={2}
                  placeholder="Lors d'un lancement, refonte de marque, changement de positionnement..."
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditModal({ open: false, type: null, data: null })}>Annuler</Button>
            <Button onClick={handleSave} className="bg-gradient-to-r from-cyan-500 to-blue-600"><Save className="w-4 h-4 mr-2" />Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Modal */}
      <Dialog open={deleteModal.open} onOpenChange={(open) => !open && setDeleteModal({ open: false, type: null, id: null })}>
        <DialogContent><DialogHeader><DialogTitle>Confirmer la suppression</DialogTitle></DialogHeader>
          <p>Êtes-vous sûr de vouloir supprimer cet élément ?</p>
          <DialogFooter><Button variant="outline" onClick={() => setDeleteModal({ open: false, type: null, id: null })}>Annuler</Button><Button variant="destructive" onClick={handleDelete}>Supprimer</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CMSServicesPage;
